import {
  BOLD_FONT,
  ITALIC_FONT,
  type Line,
  linesToHtml,
  SAME_LINE_TOLERANCE,
} from './text-layout'

const styleValue = (element: Element, property: string) =>
  element
    .getAttribute('style')
    ?.match(new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`))?.[1]
    ?.trim()

const fontOf = (element: Element) => {
  const family = styleValue(element, 'font-family') ?? ''
  const weight = styleValue(element, 'font-weight') ?? ''
  const style = styleValue(element, 'font-style') ?? ''

  return {
    size: parseFloat(styleValue(element, 'font-size') ?? '') || 0,
    bold: BOLD_FONT.test(family) || /bold|[6-9]00/.test(weight),
    italic: ITALIC_FONT.test(family) || /italic|oblique/.test(style),
  }
}

/**
 * A selection inside a single page is copied without its <svg>, so the page
 * is then inferred from the text moving back up to the top.
 */
const pageOf = (text: Element, y: number, last: Line | undefined) =>
  text.closest('svg') ??
  (last && y > last.y - SAME_LINE_TOLERANCE ? last.page : {})

const readLines = (texts: Element[]) => {
  const lines: Line[] = []

  for (const text of texts) {
    const spans = text.querySelectorAll('tspan')
    const parts = spans.length ? [...spans] : [text]

    for (const part of parts) {
      const y = Number(part.getAttribute('y') ?? text.getAttribute('y'))
      if (!Number.isFinite(y)) return null

      const { size, bold, italic } = fontOf(part)
      const run = { text: part.textContent ?? '', bold, italic }
      const last = lines[lines.length - 1]
      const page = pageOf(text, y, last)

      if (last?.page === page && Math.abs(last.y - y) < SAME_LINE_TOLERANCE) {
        last.runs.push(run)
        last.size = Math.max(last.size, size)
      } else lines.push({ page, y, size, runs: [run] })
    }
  }
  return lines
}

/**
 * Document previews that draw each PDF page as an SVG (the Jira attachment
 * viewer) copy one <text> element per visual line and no paragraph markup, so
 * Quill would run every line of the document together into one paragraph.
 * Paragraphs are rebuilt from the line positions instead.
 *
 * Returns null for any other HTML, which Quill then converts as usual.
 */
export function normalizeSvgTextLayer(html: string): string | null {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const texts = [...doc.body.querySelectorAll('text')]
  if (!texts.length) return null

  const strip = (value: string) => value.replace(/\s+/g, '')
  const layerText = texts.map(text => text.textContent ?? '').join('')
  if (strip(doc.body.textContent ?? '') !== strip(layerText)) return null

  const lines = readLines(texts)
  return lines && linesToHtml(lines)
}
